import React, { useState, useEffect } from "react";
import { Button, IconButton, Box, TextField, Badge, Typography, Stack, Autocomplete, Skeleton, } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import FSearchInput from "../inputs/SSearchInput";
import { useTranslation } from "react-i18next";
import { Filter } from "iconsax-react";
import FDialog from "../dialog/FDialog";
import { SMenu } from "../menu/SMenu";
import { SButton } from "../buttons";
import { jEnd } from "../style/style";
import { FDateRangePickerField } from "../date-range-field";
import { useForm } from "react-hook-form";
import { Tab, Tabs } from "@mui/material";
import { Icon } from "@iconify/react";
export const GridFilters = ({ columns, filters, onChange, subFilter, searchProp, handleSearchChange, hideSearch, hideFilter, title, rightSection, tabs, selectedTab, onTabChange, sortState: externalSortState, onSortChange, onApplyFilters, onClearFilters, onFilterOpen, loading, }) => {
    const { t } = useTranslation(["common", "form", "validation"]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [localFilters, setLocalFilters] = useState(filters);
    const [filterInputValues, setFilterInputValues] = useState({});
    const localFiltersRef = React.useRef(localFilters);
    const [sortAnchorEl, setSortAnchorEl] = useState(null);
    const sortState = externalSortState || null;
    React.useEffect(() => {
        localFiltersRef.current = localFilters;
    }, [localFilters]);
    const dateRangeColumn = columns.find((col) => col.filterable && col.filterType === "dateRange");
    // Use filterField if provided, otherwise fall back to field
    const allFilterFields = columns
        .filter((col) => col.filterable)
        .map((col) => col.filterField || col.field);
    const dateRangeDefaults = columns
        .filter((col) => col.filterable && col.filterType === "dateRange")
        .reduce((acc, col) => {
        acc[`dateRange_${col.field}`] = [];
        return acc;
    }, {});
    const { control } = useForm({
        defaultValues: Object.assign({ dateRange: [] }, dateRangeDefaults),
    });
    useEffect(() => {
        setLocalFilters(filters);
        localFiltersRef.current = filters;
    }, [filters]);
    const handleOpen = () => {
        setLocalFilters(filters);
        localFiltersRef.current = filters;
        setFilterInputValues({});
        setIsDialogOpen(true);
        if (onFilterOpen) {
            onFilterOpen();
        }
    };
    const handleClose = () => {
        setIsDialogOpen(false);
    };
    const handleSortClick = (event) => {
        setSortAnchorEl(event.currentTarget);
    };
    const handleSortClose = () => {
        setSortAnchorEl(null);
    };
    const handleSortSelect = (field) => {
        // Toggle sort order if same field, otherwise start with ascending
        let newOrder = "asc";
        if ((sortState === null || sortState === void 0 ? void 0 : sortState.field) === field) {
            newOrder = sortState.order === "asc" ? "desc" : "asc";
        }
        if (onSortChange) {
            onSortChange(field, newOrder);
        }
        // Don't close the menu - let user toggle multiple times
    };
    const handleFilterChange = (field, value) => {
        // Use functional update and sync ref immediately to avoid stale closure issues
        setLocalFilters((prev) => {
            const newFilters = Object.assign(Object.assign({}, prev), { [field]: value });
            localFiltersRef.current = newFilters; // Update ref synchronously
            return newFilters;
        });
    };
    const handleClearFilters = () => {
        if (onClearFilters) {
            // Use custom clear handler if provided (preserves isActive based on tab)
            onClearFilters();
            // The cleared state will be reflected through filters prop via useEffect
            // Reset local filters immediately to empty state
            setLocalFilters((prev) => {
                const cleared = Object.fromEntries(Object.keys(prev).map((key) => [key, ""]));
                localFiltersRef.current = cleared; // Update ref synchronously
                return cleared;
            });
        }
        else {
            // Default behavior: clear all filters
            setLocalFilters((prev) => {
                const cleared = Object.fromEntries(Object.keys(prev).map((key) => [key, ""]));
                localFiltersRef.current = cleared; // Update ref synchronously
                onChange(cleared);
                return cleared;
            });
        }
        handleClose();
    };
    const handleApplyFilters = () => {
        // Use ref to get the latest localFilters value (avoids stale closure)
        const currentFilters = localFiltersRef.current;
        // Build filters object with all fields - empty values will be removed by reducer
        const filtersToApply = allFilterFields.reduce((acc, field) => {
            const value = currentFilters[field];
            // Include empty values so reducer knows to delete them
            acc[field] = value !== null && value !== void 0 ? value : "";
            return acc;
        }, {});
        // Also preserve any existing filters that aren't column-based (like globalSearch, isActive)
        Object.entries(currentFilters).forEach(([key, value]) => {
            if (!allFilterFields.includes(key)) {
                filtersToApply[key] = value;
            }
        });
        onChange(filtersToApply);
        // Call the onApplyFilters callback if provided, passing the filters to apply
        if (onApplyFilters) {
            onApplyFilters(filtersToApply);
        }
        handleClose();
    };
    // Only count keys that correspond to an actual filterable column. Some pages
    // (e.g. Dashboard's active/inactive tab) stash bookkeeping like `status` or
    // `isActive` in the same filters object even though there's no visible
    // filter control for it (its column is filterable: false) — that shouldn't
    // count as a user-applied filter.
    const filterableFieldSet = new Set(allFilterFields);
    const filteredCount = Object.keys(filters).filter((key) => filterableFieldSet.has(key) &&
        filters[key] &&
        (Array.isArray(filters[key])
            ? filters[key].length > 0
            : filters[key] !== "") &&
        key !== "globalSearch").length;
    // Generate sort menu items from sortable columns
    const sortMenuItems = React.useMemo(() => columns
        .filter((col) => col.sortable === true)
        .map((col) => {
        const isActive = (sortState === null || sortState === void 0 ? void 0 : sortState.field) === col.field;
        const isAsc = isActive && (sortState === null || sortState === void 0 ? void 0 : sortState.order) === "asc";
        const icon = isActive ? (isAsc ? " ↑" : " ↓") : "";
        return {
            label: `${col.headerName}${icon}`,
            onClick: () => handleSortSelect(col.field),
            selected: isActive,
        };
    }), [columns, sortState]);
    const renderFilterFields = () => (React.createElement(Box, null,
        React.createElement(Box, { sx: Object.assign(Object.assign({}, jEnd), { mb: 1.5 }) },
            React.createElement(SButton, { variant: "soft", onClick: handleClearFilters }, "Clear")),
        columns
            .filter((col) => col.filterable)
            .map((col) => {
            var _a, _b;
            // Use filterField if provided, otherwise fall back to field
            const filterKey = col.filterField || col.field;
            return (React.createElement(Box, { key: col.field, sx: { mb: 1 } },
                col.filterType === "select" && (React.createElement(Autocomplete, { multiple: true, limitTags: 2, fullWidth: true, options: Array.isArray(col.filterOptions) &&
                        col.filterOptions.length > 0 &&
                        typeof col.filterOptions[0] === "object"
                        ? col.filterOptions
                        : (col.filterOptions || []).map((opt) => ({
                            label: String(opt),
                            value: String(opt),
                        })), value: (() => {
                        const fieldValue = localFilters[filterKey];
                        // Normalize options
                        const options = Array.isArray(col.filterOptions) &&
                            col.filterOptions.length > 0 &&
                            typeof col.filterOptions[0] === "object"
                            ? col.filterOptions
                            : (col.filterOptions || []).map((opt) => ({
                                label: String(opt),
                                value: String(opt),
                            }));
                        if (Array.isArray(fieldValue)) {
                            return options.filter((opt) => fieldValue.includes(opt.value));
                        }
                        else if (fieldValue !== undefined &&
                            fieldValue !== null &&
                            fieldValue !== "") {
                            return options.filter((opt) => opt.value === fieldValue);
                        }
                        return [];
                    })(), inputValue: (_a = filterInputValues[filterKey]) !== null && _a !== void 0 ? _a : "", onInputChange: (_, v, reason) => {
                        // Ignore MUI's automatic "reset" fires that happen when the
                        // `value` prop reference changes on every render (IIFE pattern).
                        // Handling selection-clear is done in onChange below.
                        if (reason !== "reset") {
                            setFilterInputValues((prev) => (Object.assign(Object.assign({}, prev), { [filterKey]: v })));
                        }
                    }, onChange: (_, newValue) => {
                        handleFilterChange(filterKey, newValue.map((opt) => opt.value));
                        setFilterInputValues((prev) => (Object.assign(Object.assign({}, prev), { [filterKey]: "" })));
                    }, getOptionLabel: (option) => option.label, isOptionEqualToValue: (option, value) => option.value === value.value, renderInput: (params) => (React.createElement(TextField, Object.assign({}, params, { variant: "filled", label: col.headerName }))) })),
                col.filterType === "text" && (React.createElement(TextField, { fullWidth: true, 
                    // size="small"
                    variant: "filled", label: col.headerName, value: localFilters[filterKey] || "", onChange: (e) => handleFilterChange(filterKey, e.target.value) })),
                col.filterType === "date" && (React.createElement(DatePicker, { enableAccessibleFieldDOMStructure: false, label: col.headerName, value: Array.isArray(localFilters[filterKey])
                        ? null
                        : localFilters[filterKey]
                            ? dayjs(localFilters[filterKey])
                            : null, onChange: (date) => handleFilterChange(filterKey, date ? dayjs(date).format("YYYY-MM-DD") : ""), slotProps: {
                        textField: {
                            // size: "small",
                            fullWidth: true,
                            variant: "filled",
                        },
                    } })),
                col.filterType === "numberRange" && (() => {
                    const [min = "", max = ""] = String(localFilters[filterKey] || "").split(",");
                    const setRange = (nextMin, nextMax) => handleFilterChange(filterKey, nextMin || nextMax ? `${nextMin},${nextMax}` : "");
                    return (React.createElement(Box, { sx: { display: "flex", gap: 1 } },
                        React.createElement(TextField, { fullWidth: true, variant: "filled", type: "number", label: `${col.headerName} (min)`, value: min, inputProps: { min: 0 }, onChange: (e) => setRange(e.target.value, max) }),
                        React.createElement(TextField, { fullWidth: true, variant: "filled", type: "number", label: `${col.headerName} (max)`, value: max, inputProps: { min: 0 }, onChange: (e) => setRange(min, e.target.value) })));
                })(),
                col.filterType === "dateRange" && (React.createElement(Box, { sx: { position: "relative", zIndex: 1 } },
                    React.createElement(FDateRangePickerField, { label: col.headerName || "Date Range", required: false, control: control, name: `dateRange_${filterKey}`, value: localFilters[filterKey] || "", handleSelectedRange: (startDate, endDate) => {
                            const dateRangeValue = startDate && endDate ? `${startDate},${endDate}` : "";
                            handleFilterChange(filterKey, dateRangeValue);
                        } }))),
                col.filterType === "asyncSelect" && (React.createElement(Autocomplete, { multiple: true, limitTags: 2, fullWidth: true, loading: col.filterLoading, options: Array.isArray(col.filterOptions) &&
                        col.filterOptions.length > 0 &&
                        typeof col.filterOptions[0] === "object"
                        ? col.filterOptions
                        : (col.filterOptions || []).map((opt) => ({
                            label: String(opt),
                            value: String(opt),
                        })), value: (() => {
                        const fieldValue = localFilters[filterKey];
                        // Normalize options
                        const options = Array.isArray(col.filterOptions) &&
                            col.filterOptions.length > 0 &&
                            typeof col.filterOptions[0] === "object"
                            ? col.filterOptions
                            : (col.filterOptions || []).map((opt) => ({
                                label: String(opt),
                                value: String(opt),
                            }));
                        // Build selected chips directly from localFilters — always show
                        // even if the item is no longer in the current options list
                        // (e.g., re-fetch after selection cleared the list momentarily).
                        if (Array.isArray(fieldValue)) {
                            return fieldValue.map((val) => {
                                const found = options.find((o) => o.value === val);
                                return found !== null && found !== void 0 ? found : { value: val, label: val };
                            });
                        }
                        else if (fieldValue !== undefined &&
                            fieldValue !== null &&
                            fieldValue !== "") {
                            const found = options.find((o) => o.value === fieldValue);
                            return [found !== null && found !== void 0 ? found : { value: fieldValue, label: fieldValue }];
                        }
                        return [];
                    })(), inputValue: (_b = filterInputValues[filterKey]) !== null && _b !== void 0 ? _b : "", onChange: (_, newValue) => {
                        handleFilterChange(filterKey, newValue.map((opt) => opt.value));
                        setFilterInputValues((prev) => (Object.assign(Object.assign({}, prev), { [filterKey]: "" })));
                        if (col.onFilterInputChange)
                            col.onFilterInputChange("");
                    }, onInputChange: (_, inputValue, reason) => {
                        if (reason !== "reset") {
                            setFilterInputValues((prev) => (Object.assign(Object.assign({}, prev), { [filterKey]: inputValue })));
                        }
                        if (col.onFilterInputChange && (reason === "input" || reason === "clear")) {
                            col.onFilterInputChange(inputValue);
                        }
                    }, getOptionLabel: (option) => option.label, isOptionEqualToValue: (option, value) => option.value === value.value, filterOptions: (x) => x, renderInput: (params) => (React.createElement(TextField, Object.assign({}, params, { variant: "filled", label: col.headerName, InputProps: Object.assign(Object.assign({}, params.InputProps), { endAdornment: (React.createElement(React.Fragment, null,
                                col.filterLoading ? (React.createElement(Icon, { icon: "eos-icons:loading", width: 20, height: 20 })) : null,
                                params.InputProps.endAdornment)) }) }))) }))));
        })));
    return (React.createElement(Box, null,
        rightSection && !tabs && (React.createElement(Typography, { variant: "h4", color: "#00478D" }, loading ? React.createElement(Skeleton, { width: 120 }) : title)),
        React.createElement(Stack, { direction: { xs: "column-reverse", sm: "row" }, spacing: 1, alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between" },
            tabs && tabs.length > 0 ? (React.createElement(Tabs, { value: selectedTab || false, onChange: (_, newValue) => onTabChange === null || onTabChange === void 0 ? void 0 : onTabChange(newValue), variant: "scrollable", scrollButtons: "auto" }, tabs.map((tab) => (React.createElement(Tab, { key: tab.value, value: tab.value, label: tab.label }))))) : rightSection ? (rightSection) : (React.createElement(Typography, { variant: "h4", color: "#00478D" }, loading ? React.createElement(Skeleton, { width: 120 }) : title)),
            React.createElement(Stack, { direction: "row", spacing: { xs: 0.5, sm: 1 }, alignItems: "center", sx: {
                    justifyContent: { xs: "flex-end", sm: "flex-end" },
                    width: { xs: "100%", sm: "auto" },
                } },
                !hideSearch && (React.createElement(Box, { sx: {
                        minWidth: { xs: 200, sm: 200 },
                        flex: { xs: 1, sm: "none" },
                        width: { xs: "100%", sm: "auto" },
                    } },
                    React.createElement(FSearchInput, { value: searchProp || "", hiddenLabel: true, onChange: handleSearchChange, sx: { width: "100%" } }))),
                subFilter,
                !hideFilter && (React.createElement(Badge, { badgeContent: filteredCount, color: "primary" },
                    React.createElement(React.Fragment, null,
                        React.createElement(IconButton, { onClick: handleOpen, "aria-label": t("common:actions.filter"), sx: {
                                display: { xs: "inline-flex", sm: "none" },
                                width: "53px",
                                height: "53px",
                                borderRadius: "50px",
                                backgroundColor: "#919EAB14",
                                "&:hover": {
                                    backgroundColor: "#919EAB14",
                                },
                            } },
                            React.createElement(Filter, { width: "20px", height: "20px" })),
                        React.createElement(Button, { variant: "soft", color: "inherit", size: "medium", onClick: handleOpen, endIcon: React.createElement(Filter, { width: "20px", height: "20px" }), sx: {
                                display: { xs: "none", sm: "inline-flex" },
                                minWidth: 100,
                                height: "53px",
                                borderRadius: "50px",
                            } }, t("common:actions.filter"))))),
                sortMenuItems.length > 0 && (React.createElement(React.Fragment, null,
                    React.createElement(IconButton, { onClick: handleSortClick, "aria-label": "Sort", sx: {
                            display: { xs: "inline-flex", sm: "none" },
                            width: "53px",
                            height: "53px",
                            borderRadius: "50px",
                            backgroundColor: "#919EAB14",
                            "&:hover": {
                                backgroundColor: "#919EAB14",
                            },
                        } },
                        React.createElement(Icon, { icon: "hugeicons:arrow-data-transfer-vertical" })),
                    React.createElement(Button, { variant: "soft", color: "inherit", size: "medium", onClick: handleSortClick, endIcon: React.createElement(Icon, { icon: "hugeicons:arrow-data-transfer-vertical" }), sx: {
                            display: { xs: "none", sm: "inline-flex" },
                            minWidth: 90,
                            height: "53px",
                            borderRadius: "50px",
                        } }, "Sort"))))),
        React.createElement(SMenu, { anchorEl: sortAnchorEl, open: Boolean(sortAnchorEl), handleClose: handleSortClose, menuItems: sortMenuItems, closeOnClick: false }),
        React.createElement(FDialog, { isOpen: isDialogOpen, onClose: handleClose, onSubmit: handleApplyFilters, title: t("common:actions.filters"), confirmText: t("common:actions.apply"), cancelText: t("common:actions.cancel"), maxWidth: "sm", actions: React.createElement(Stack, { direction: "row", spacing: 1, sx: { width: "100%", mt: 2 }, justifyContent: "flex-end" },
                React.createElement(Button, { variant: "outlined", color: "inherit", onClick: handleClose }, "Close"),
                React.createElement(Button, { variant: "contained", onClick: handleApplyFilters }, t("common:actions.apply"))) },
            React.createElement(Box, { sx: { "& .rmdp-wrapper": { zIndex: 9999 } } }, renderFilterFields()))));
};
